import { UserRole, Permission, RolePermissions } from '../types';

// Define role-based permissions
export const ROLE_PERMISSIONS: RolePermissions = {
  admin: [
    // Parts permissions - full access
    'parts.create',
    'parts.read',
    'parts.update',
    'parts.delete',
    
    // Stock permissions - full access
    'stock.create',
    'stock.read',
    'stock.update',
    'stock.delete',
    'stock.edit_quantity',
    'stock.edit_old_data',
    
    // Categories permissions - full access
    'categories.create',
    'categories.read',
    'categories.update',
    'categories.delete',
    
    // Suppliers permissions - full access
    'suppliers.create',
    'suppliers.read',
    'suppliers.update',
    'suppliers.delete',
    
    // Locations permissions - full access
    'locations.create',
    'locations.read',
    'locations.update',
    'locations.delete',
    
    // Reports permissions - full access
    'reports.read',
    'reports.export',
    
    // User management permissions - full access
    'users.create',
    'users.read',
    'users.update',
    'users.delete',
  ],
  
  staff: [
    // Parts permissions - limited access
    'parts.read',
    'parts.create', // Can add new parts
    'parts.update', // Can update part details (but not quantities)
    
    // Stock permissions - restricted access
    'stock.create', // Can create new stock movements
    'stock.read',
    'stock.update', // Can update recent stock movements (within 3 days)
    // NO 'stock.delete' - staff cannot delete stock movements
    // NO 'stock.edit_quantity' - staff cannot edit quantities directly
    // NO 'stock.edit_old_data' - staff cannot edit data older than 3 days
    
    // Categories permissions - read-only
    'categories.read',
    
    // Suppliers permissions - read-only
    'suppliers.read',
    
    // Locations permissions - read-only
    'locations.read',
    
    // Reports permissions - read-only
    'reports.read',
  ],
};

/**
 * Check if a user has a specific permission
 */
export const hasPermission = (userRole: UserRole, permission: Permission): boolean => {
  const rolePermissions = ROLE_PERMISSIONS[userRole];
  return rolePermissions ? rolePermissions.includes(permission) : false;
};

/**
 * Check if a user has any of the specified permissions
 */
export const hasAnyPermission = (userRole: UserRole, permissions: Permission[]): boolean => {
  return permissions.some(permission => hasPermission(userRole, permission));
};

/**
 * Check if a user has all of the specified permissions
 */
export const hasAllPermissions = (userRole: UserRole, permissions: Permission[]): boolean => {
  return permissions.every(permission => hasPermission(userRole, permission));
};

/**
 * Get all permissions for a role
 */
export const getRolePermissions = (userRole: UserRole): Permission[] => {
  return ROLE_PERMISSIONS[userRole] || [];
};

/**
 * Check if user is admin
 */
export const isAdmin = (userRole: UserRole): boolean => {
  return userRole === 'admin';
};

/**
 * Check if user is staff
 */
export const isStaff = (userRole: UserRole): boolean => {
  return userRole === 'staff';
};

/**
 * Get permission description for UI display
 */
export const getPermissionDescription = (permission: Permission): string => {
  const descriptions: Record<Permission, string> = {
    'parts.create': 'Create new parts',
    'parts.read': 'View parts',
    'parts.update': 'Edit parts',
    'parts.delete': 'Delete parts',
    'stock.create': 'Create stock movements',
    'stock.read': 'View stock levels',
    'stock.update': 'Update stock levels',
    'stock.delete': 'Delete stock movements',
    'stock.edit_quantity': 'Edit quantities directly',
    'stock.edit_old_data': 'Edit old data (older than 3 days)',
    'categories.create': 'Create categories',
    'categories.read': 'View categories',
    'categories.update': 'Edit categories',
    'categories.delete': 'Delete categories',
    'suppliers.create': 'Create suppliers',
    'suppliers.read': 'View suppliers',
    'suppliers.update': 'Edit suppliers',
    'suppliers.delete': 'Delete suppliers',
    'locations.create': 'Create locations',
    'locations.read': 'View locations',
    'locations.update': 'Edit locations',
    'locations.delete': 'Delete locations',
    'reports.read': 'View reports',
    'reports.export': 'Export reports',
    'users.create': 'Create users',
    'users.read': 'View users',
    'users.update': 'Edit users',
    'users.delete': 'Delete users',
  };
  
  return descriptions[permission] || permission;
};

/**
 * Get role display name
 */
export const getRoleDisplayName = (role: UserRole): string => {
  const displayNames: Record<UserRole, string> = {
    admin: 'Administrator',
    staff: 'Staff',
  };
  
  return displayNames[role] || role;
};

/**
 * Check if user can edit data based on age restrictions
 */
export const canEditDataByAge = (userRole: UserRole, dataDate: string | Date): boolean => {
  if (userRole === 'admin') {
    return true; // Admins can edit any data regardless of age
  }
  
  if (userRole === 'staff') {
    const dataDateTime = new Date(dataDate);
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
    
    return dataDateTime >= threeDaysAgo; // Staff can only edit data from last 3 days
  }
  
  return false;
};

/**
 * Check if user can delete records
 */
export const canDelete = (userRole: UserRole): boolean => {
  return userRole === 'admin';
};

/**
 * Check if user can edit quantities directly
 */
export const canEditQuantity = (userRole: UserRole): boolean => {
  return userRole === 'admin';
};

/**
 * Check if user can edit old data (older than 3 days)
 */
export const canEditOldData = (userRole: UserRole): boolean => {
  return userRole === 'admin';
};

/**
 * Get user role restrictions summary
 */
export const getUserRestrictions = (userRole: UserRole) => {
  const restrictions = {
    canDelete: canDelete(userRole),
    canEditQuantity: canEditQuantity(userRole),
    canEditOldData: canEditOldData(userRole),
    maxEditAge: userRole === 'admin' ? null : 3, // days
  };
  
  return restrictions;
};

/**
 * Check if a stock movement can be edited by user
 */
export const canEditStockMovement = (userRole: UserRole, movementDate: string | Date): boolean => {
  if (userRole === 'admin') {
    return true;
  }
  
  if (userRole === 'staff') {
    return canEditDataByAge(userRole, movementDate);
  }
  
  return false;
};

/**
 * Check if a stock movement can be deleted by user
 */
export const canDeleteStockMovement = (userRole: UserRole): boolean => {
  return canDelete(userRole);
};
